import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import store from "redux/store";
import { useLanguage } from "lang/Translate";

// assets
import defaultAirportImage from "assets/images/airport.svg";

const useAirport = (code, country = null) => {
	const airportList = useSelector(state => state.airports);

	if (country) {
		return Object.values(airportList[country]).find(airport => airport.ident === code);
	}

	const allAirports = Object.values(airportList)
		.map(country => Object.values(country))
		.flat();

	if (code == null) return null;
	return allAirports.find(airport => {
		if (typeof code === "string") {
			return airport.ident === code;
		} else {
			return airport.id === code;
		}
	});
};

function useAirportType(typeId) {
	const currLanguage = useLanguage();
	const airportType = useMemo(() => {
		if (currLanguage === "PL") {
			switch (typeId) {
				case "small_airport":
					return "małe";
				case "medium_airport":
					return "średnie";
				case "large_airport":
					return "duże";
				case "huge_airport":
					return "ogromne";
				default:
					return "nieznane";
			}
		}

		switch (typeId) {
			case "small_airport":
				return "small";
			case "medium_airport":
				return "medium";
			case "large_airport":
				return "large";
			case "huge_airport":
				return "huge";
			default:
				return "unknown";
		}
	}, [typeId, currLanguage]);

	return airportType;
}

function useAirportImage(wikipediaLink) {
	const [url, setUrl] = useState(null);
	const [loaded, setLoaded] = useState(false);

	const fetchImage = useCallback(async wikipediaLink => {
		setUrl(null);
		setLoaded(false);

		const splitted = wikipediaLink.split("/");
		const title = splitted[splitted.length - 1];

		const fromCache = localStorage.getItem(title);
		try {
			await preloadImage(fromCache);
			setUrl(fromCache);
			setLoaded(true);
			return;
		} catch (e) {}

		// const response = await fetch(
		// 	`https://en.wikipedia.org/w/api.php?action=query&format=json&prop=pageimages&titles=${title}&utf8=1&lllimit=500&origin=*&redirects=1&piprop=original`
		// );
		// const json = await response.json();
		// console.log(json);
		// const url = Object.values(json.query.pages)[0].original?.source;

		const exclude = ["svg", "logo", "boeing", "airbus", "map", "check", "station", "aviacionavion.png", "bus", "cargo"];
		const prefer = [
			"airport",
			"terminal",
			"runway",
			"aerial",
			"aeroporto",
			"aeroport",
			"aeropuerto",
			"panorama",
			"flughafen"
		];

		const response = await fetch(
			`https://en.wikipedia.org/w/api.php?action=query&format=json&prop=images&titles=${title}&utf8=1&imlimit=500&origin=*&redirects=1&piprop=original`
		);
		const allImagesJson = await response.json();
		const allImages = Object.values(allImagesJson.query.pages)[0]
			.images.map(i => i.title.split("File:")[1])
			.filter(i => {
				i = i.toLowerCase();
				return !i.match(/19\d{2}/) && !exclude.map(e => i.includes(e)).includes(true);
			});

		if (allImages.length === 0) {
			setLoaded(true);
			return;
		}

		const preferedImage = allImages.find(image => prefer.some(str => image.includes(str)));

		const response2 = await fetch(
			`https://en.wikipedia.org/w/api.php?action=query&titles=Image:${preferedImage ??
				allImages[0]}&prop=imageinfo&iiprop=url&format=json&origin=*`
		);
		const json2 = await response2.json();
		const url = json2.query.pages[Object.keys(json2.query.pages)[0]].imageinfo[0].url;
		localStorage.setItem(title, url);

		if (url) await preloadImage(url);
		setUrl(url);
		setLoaded(true);
	}, []);

	useEffect(() => {
		if (wikipediaLink == null || wikipediaLink === "") {
			setLoaded(true);
			setUrl(null);
			return;
		}

		fetchImage(wikipediaLink);
	}, [wikipediaLink, fetchImage]);

	return loaded ? url ?? defaultAirportImage : null;
}

const preloadImage = src => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.src = src;
		image.style.display = "none";
		image.onload = () => {
			document.body.removeChild(image);
			resolve();
		};

		image.onerror = reject;
		document.body.appendChild(image);
	});
};

const getAirport = (airportList, code, country = null) => {
	if (!airportList || typeof airportList !== "object") {
		throw new TypeError(`Expected airportList of type Object, but got ${typeof airportList}`);
	}

	if (country) {
		return Object.values(airportList[country]).find(airport => airport.ident === code);
	}

	const allAirports = Object.values(airportList)
		.map(country => Object.values(country))
		.flat();

	return allAirports.find(airport => airport.ident === code);
};

const getAirportById = (airportList, airportId, country = null) => {
	if (!airportList || typeof airportList !== "object") {
		throw new TypeError(`Expected airportList of type Object, but got ${typeof airportList}`);
	}

	if (country) {
		return Object.values(airportList[country]).find(airport => airport.id === airportId);
	}

	const allAirports = Object.values(airportList)
		.map(country => Object.values(country))
		.flat();

	// console.log(airportList, allAirports.map(a => a.id).includes(airportId));

	const airport = allAirports.find(a => a.id === airportId);

	if (store.getState().airlineInfo.hubs[0] !== airport.id) {
		const { longitude_deg: hub_lng } = getAirportById(store.getState().airports, store.getState().airlineInfo.hubs[0]);
		if (airport.longitude_deg < hub_lng - 180 || airport.longitude_deg > hub_lng + 180) {
			airport.longitude_deg += 360 * (hub_lng / Math.abs(hub_lng));
		}
	}

	return airport;
};

function useHub(hubIndex = 0) {
	const hubs = useSelector(state => state.airlineInfo.hubs);

	if (hubs != null && hubs[hubIndex] == null) {
		throw new TypeError("hubIndex is out of range!");
	}

	const details = useAirport(hubs == null ? null : hubs[hubIndex]);
	return details;
}

export { getAirport, useAirport, useAirportType, getAirportById, useAirportImage, useHub };
export default useAirport;
